'use client'

import { RegisterForm, type RegisterFormSuccessPayload } from '@/components/Auth/RegisterForm'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { isAdult } from '@/lib/registrationAge'
import { logClickEvent } from '@/utils/eventLogging'
import { Check } from 'lucide-react'
import { useRouter } from 'next/navigation'

export type RegisterDialogSource = 'guest_banner' | 'profile_cta' | 'navigation' | 'document_upload_cta'

interface RegisterDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    source: RegisterDialogSource
    onSuccess?: (payload: RegisterFormSuccessPayload) => void
}

export function RegisterDialog({ open, onOpenChange, source, onSuccess }: RegisterDialogProps) {
    const router = useRouter()

    const handleOpenChange = (nextOpen: boolean) => {
        if (!nextOpen) {
            logClickEvent({ action: 'register_dialog_close', metadata: { source } })
        }
        onOpenChange(nextOpen)
    }

    const handleSuccess = (payload: RegisterFormSuccessPayload) => {
        onSuccess?.(payload)
        onOpenChange(false)
        router.replace(`/auth/registration-info?status=${isAdult(payload.age) ? 'adult' : 'parent'}`)
    }

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent
                className="!border-white/10 !bg-[#1e2330] text-white shadow-2xl sm:max-w-xl sm:rounded-2xl"
                overlayClassName="bg-black/40 backdrop-blur-sm"
                onInteractOutside={(e) => e.preventDefault()}
            >
                <DialogHeader className="mb-2">
                    <div className="py-4 gradient-text">
                        <DialogTitle>Account erstellen</DialogTitle>
                    </div>
                    <DialogDescription>
                        Speichere deinen Fortschritt und mach jederzeit dort weiter, wo du aufgehört hast.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex text-center flex-wrap-break-word gap-2 mb-3">
                    {['Gespräch speichern', 'Fortschritt behalten', 'Jederzeit weitermachen'].map((text) => (
                        <span
                            key={text}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-primary"
                        >
                            <Check className="h-3 w-3" />
                            {text}
                        </span>
                    ))}
                </div>

                <RegisterForm
                    className="space-y-5"
                    fieldIdPrefix={`register-${source}`}
                    includeTransferData
                    useEmailAsUsername
                    showInlineErrorMessage
                    submitLabel="Kostenlosen Account erstellen"
                    onSuccess={handleSuccess}
                />

                <button
                    type="button"
                    className="mx-auto block text-sm text-muted-foreground underline underline-offset-4 transition-colors hover:text-foreground"
                    onClick={() => handleOpenChange(false)}
                >
                    Ohne Account weiter
                </button>
            </DialogContent>
        </Dialog>
    )
}
